/**
 * Session Report — multi-session analytics list, styled like Admin Reports.
 *
 * Filters: Track, Course, Batch, Branch, Date, Trainer, Session Category, Status.
 * Table: checkbox, sortable columns, search, export.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { PiSortAscendingLight, PiSortDescendingLight } from "react-icons/pi";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { MdFileDownload } from "react-icons/md";
import { Spinner } from "react-bootstrap";
import * as XLSX from "xlsx";
import { fetchAllTracksForCourses } from "../../../utils/trackUtils";
import { fetchCoursesByTrack } from "../../../utils/courseUtils";
import { fetchBatchesByCourseSingle } from "../../../utils/batchUtils";
import { LIVE_SESSION_CATEGORY_OPTIONS } from "../../../codelive/create/config/liveSessionTypes";
import {
  createSessionNameSlug,
  getSessionAnalyticsPath,
} from "./sessionUtils/sessionUtils";
import type { Track, Course, Batch } from "./sessionUtils/sessionTypes";
import {
  fetchTrainersReport,
  type TrainersReportRow,
} from "./trainersReportApi";
import GraspFeedbacksDialog from "./GraspFeedbacksDialog";
import type { GraspFeedbackRow } from "./sessionAnalyticsReportApi";

const STATUS_OPTIONS = [
  { value: "completed", label: "Completed" },
  { value: "ongoing", label: "Ongoing" },
  { value: "upcoming", label: "Upcoming" },
  { value: "all", label: "All" },
];

const selectStyle: React.CSSProperties = {
  height: 36,
  fontSize: 14,
  fontFamily: "inherit",
};

const headerCellStyle: React.CSSProperties = {
  backgroundColor: "#f5f5f5",
  borderRight: "1px solid #e0e0e0",
  fontWeight: 600,
  fontSize: 12,
  padding: "8px 12px",
  color: "#666",
  whiteSpace: "nowrap",
  position: "sticky",
  top: 0,
  zIndex: 10,
};

const groupHeaderStyle: React.CSSProperties = {
  ...headerCellStyle,
  textAlign: "center",
};

const subHeaderStyle: React.CSSProperties = {
  ...headerCellStyle,
  top: 37,
  fontSize: 11,
  fontWeight: 600,
};

const bodyCellStyle: React.CSSProperties = {
  padding: "6px 12px",
  borderRight: "1px solid #e0e0e0",
  fontSize: 13,
  whiteSpace: "nowrap",
};

type SortKey =
  | "session_name"
  | "student_count"
  | "planned_minutes"
  | "actual_minutes"
  | "trainer_count"
  | "attendance_percent"
  | "distraction_percent"
  | "avg_quiz_score"
  | "grasp_understood_percent"
  | "grasp_partial_percent"
  | "grasp_did_not_percent"
  | "avg_rating";

function hasPctValue(value: number | null | undefined): boolean {
  return value != null && !Number.isNaN(Number(value));
}

function pct(value: number | null | undefined): string {
  if (!hasPctValue(value)) return "—";
  return `${Math.round(Number(value))}%`;
}

function graspCellStyle(
  value: number | null | undefined,
  clickable: React.CSSProperties,
  plain: React.CSSProperties,
): React.CSSProperties {
  return hasPctValue(value) ? clickable : plain;
}

function mins(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const totalSec = Math.max(0, Math.round(Number(value) * 60));
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function num(value: number | null | undefined): number {
  if (value == null || Number.isNaN(Number(value))) return -1;
  return Number(value);
}

/** Per-row pdf/excel column — hide until needed again. */
const SHOW_ROW_DOWNLOAD_COLUMN = false;

const FILTERS_STORAGE_KEY = "trainersReportFilters";

type SavedReportFilters = {
  track: string;
  course: string;
  batch: string;
  branch: string;
  date: string;
  trainer: string;
  category: string;
  status: string;
  search: string;
  trainers: Array<{ id: string; name: string }>;
  branches: string[];
  courses: Course[];
  batches: Batch[];
};

const EMPTY_FILTERS: SavedReportFilters = {
  track: "",
  course: "",
  batch: "",
  branch: "",
  date: "",
  trainer: "",
  category: "",
  status: "all",
  search: "",
  trainers: [],
  branches: [],
  courses: [],
  batches: [],
};

function readSavedFilters(): SavedReportFilters {
  try {
    const raw = sessionStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return EMPTY_FILTERS;
    const parsed = JSON.parse(raw) as Partial<SavedReportFilters>;
    return {
      ...EMPTY_FILTERS,
      ...parsed,
      trainers: Array.isArray(parsed.trainers) ? parsed.trainers : [],
      branches: Array.isArray(parsed.branches) ? parsed.branches : [],
      courses: Array.isArray(parsed.courses) ? parsed.courses : [],
      batches: Array.isArray(parsed.batches) ? parsed.batches : [],
      status: parsed.status || "all",
    };
  } catch {
    return EMPTY_FILTERS;
  }
}

function writeSavedFilters(filters: SavedReportFilters) {
  try {
    sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch {
    /* ignore quota / private mode */
  }
}

const TrainersReport: React.FC = () => {
  const navigate = useNavigate();
  const [savedFilters] = useState(readSavedFilters);

  const [tracks, setTracks] = useState<Track[]>([]);
  const [courses, setCourses] = useState<Course[]>(savedFilters.courses);
  const [batches, setBatches] = useState<Batch[]>(savedFilters.batches);
  const [trainers, setTrainers] = useState<Array<{ id: string; name: string }>>(
    savedFilters.trainers,
  );
  const [branches, setBranches] = useState<string[]>(savedFilters.branches);

  const [selectedTrack, setSelectedTrack] = useState(savedFilters.track);
  const [selectedCourse, setSelectedCourse] = useState(savedFilters.course);
  const [selectedBatch, setSelectedBatch] = useState(savedFilters.batch);
  const [selectedBranch, setSelectedBranch] = useState(savedFilters.branch);
  const [selectedDate, setSelectedDate] = useState(savedFilters.date);
  const [selectedTrainer, setSelectedTrainer] = useState(savedFilters.trainer);
  const [selectedCategory, setSelectedCategory] = useState(savedFilters.category);
  const [selectedStatus, setSelectedStatus] = useState(savedFilters.status);


  const [loadingFilters, setLoadingFilters] = useState(true);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);
  const [appliedOnce, setAppliedOnce] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState<TrainersReportRow[]>([]);
  const [searchQuery, setSearchQuery] = useState(savedFilters.search);
  const [sortConfig, setSortConfig] = useState<{
    key: SortKey | "";
    direction: "ascending" | "descending" | "";
  }>({ key: "", direction: "" });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [graspFeedbacksOpen, setGraspFeedbacksOpen] = useState(false);
  const [graspPopupSessionId, setGraspPopupSessionId] = useState("");
  const [graspPopupSessionName, setGraspPopupSessionName] = useState("");
  const [graspPopupFilter, setGraspPopupFilter] = useState<
    "partial_did_not" | "partial" | "not_understood" | "understood"
  >("partial_did_not");
  const [graspFeedbacks, setGraspFeedbacks] = useState<GraspFeedbackRow[]>([]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [
    selectedTrack,
    selectedCourse,
    selectedBatch,
    selectedBranch,
    selectedDate,
    selectedTrainer,
    selectedCategory,
    selectedStatus,
    searchQuery,
  ]);

  useEffect(() => {
    writeSavedFilters({
      track: selectedTrack,
      course: selectedCourse,
      batch: selectedBatch,
      branch: selectedBranch,
      date: selectedDate,
      trainer: selectedTrainer,
      category: selectedCategory,
      status: selectedStatus,
      search: searchQuery,
      trainers,
      branches,
      courses,
      batches,
    });
  }, [
    selectedTrack,
    selectedCourse,
    selectedBatch,
    selectedBranch,
    selectedDate,
    selectedTrainer,
    selectedCategory,
    selectedStatus,
    searchQuery,
    trainers,
    branches,
    courses,
    batches,
  ]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingFilters(true);
        const trackList = await fetchAllTracksForCourses();
        if (cancelled) return;
        setTracks(Array.isArray(trackList) ? trackList : []);
      } catch (err) {
        console.error("trainers-report filters load failed", err);
        if (!cancelled) setError("Could not load filters.");
      } finally {
        if (!cancelled) setLoadingFilters(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedTrack) {
      setCourses([]);
      setSelectedCourse("");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingCourses(true);
        const list = await fetchCoursesByTrack(selectedTrack);
        if (cancelled) return;
        setCourses(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error("courses load failed", err);
        if (!cancelled) setCourses([]);
      } finally {
        if (!cancelled) setLoadingCourses(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedTrack]);

  useEffect(() => {
    if (!selectedCourse) {
      setBatches([]);
      setSelectedBatch("");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingBatches(true);
        const list = await fetchBatchesByCourseSingle(selectedCourse);
        if (cancelled) return;
        setBatches(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error("batches load failed", err);
        if (!cancelled) setBatches([]);
      } finally {
        if (!cancelled) setLoadingBatches(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCourse]);

  const handleApply = useCallback(async () => {
    setAppliedOnce(true);
    setLoadingRows(true);
    setError("");
    try {
      const result = await fetchTrainersReport({
        track_id: selectedTrack || undefined,
        course_id: selectedCourse || undefined,
        batch_id: selectedBatch || undefined,
        branch: selectedBranch || undefined,
        date: selectedDate || undefined,
        trainer_id: selectedTrainer || undefined,
        session_category: selectedCategory || undefined,
        status: selectedStatus || "all",
      });
      setRows(result.sessions);
      setSelectedIds(new Set());
    } catch (err) {
      console.error("trainers-report load failed", err);
      setError("Could not load trainers report. Try again.");
      setRows([]);
    } finally {
      setLoadingRows(false);
    }
  }, [
    selectedTrack,
    selectedCourse,
    selectedBatch,
    selectedBranch,
    selectedDate,
    selectedTrainer,
    selectedCategory,
    selectedStatus,
  ]);

  useEffect(() => {
    const trainerMap = new Map<string, string>();
    const branchSet = new Set<string>();
    for (const row of rows) {
      if (row.trainer_id && row.trainer_name) {
        trainerMap.set(row.trainer_id, row.trainer_name);
      } else if (row.trainer_name) {
        trainerMap.set(row.trainer_name, row.trainer_name);
      }
      if (row.branch) branchSet.add(row.branch);
    }
    if (trainerMap.size > 0) {
      setTrainers((prev) => {
        const merged = new Map(prev.map((t) => [t.id, t.name]));
        trainerMap.forEach((name, id) => merged.set(id, name));
        return Array.from(merged.entries()).map(([id, name]) => ({ id, name }));
      });
    }
    if (branchSet.size > 0) {
      setBranches((prev) => Array.from(new Set(prev.concat(Array.from(branchSet)))).sort());
    }
  }, [rows]);

  const handleViewReport = (row: TrainersReportRow) => {
    const slug = createSessionNameSlug(row.session_name);
    navigate(getSessionAnalyticsPath(slug, row.session_id), {
      state: {
        sessionTitle: row.session_name,
        trainerName: row.trainer_name,
        batchLabel: row.batch_name,
        durationMinutes: row.planned_minutes,
        plannedDurationMinutes: row.planned_minutes,
      },
    });
  };

  const openGraspFeedbacks = (
    row: TrainersReportRow,
    level: "partial_did_not" | "partial" | "not_understood" | "understood" = "partial_did_not",
  ) => {
    if (!row.session_id) return;
    setGraspPopupSessionId(row.session_id);
    setGraspPopupSessionName(row.session_name || "");
    setGraspPopupFilter(level);
    setGraspFeedbacks([]);
    setGraspFeedbacksOpen(true);
  };

  const graspClickCellStyle: React.CSSProperties = {
    ...bodyCellStyle,
    cursor: "pointer",
    color: "#0f172a",
    textDecoration: "underline",
    textUnderlineOffset: 2,
  };

  const filteredRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) => {
      const hay = [
        row.session_name,
        row.trainer_name,
        row.batch_name,
        row.branch,
        row.session_date,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(query);
    });
  }, [rows, searchQuery]);

  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return filteredRows;
    const key = sortConfig.key;
    const dir = sortConfig.direction === "ascending" ? 1 : -1;
    return [...filteredRows].sort((a, b) => {
      if (key === "session_name") {
        return (
          String(a.session_name || "").localeCompare(String(b.session_name || "")) *
          dir
        );
      }
      return (num(a[key]) - num(b[key])) * dir;
    });
  }, [filteredRows, sortConfig]);

  const handleSort = (key: SortKey) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: "ascending" };
      if (prev.direction === "ascending")
        return { key, direction: "descending" };
      return { key: "", direction: "" };
    });
  };

  const sortIcon = (key: SortKey) => {
    if (sortConfig.key !== key) return <HiOutlineDotsVertical size={16} />;
    return sortConfig.direction === "ascending" ? (
      <PiSortAscendingLight size={16} />
    ) : (
      <PiSortDescendingLight size={16} />
    );
  };

  const visibleIds = sortedRows.map((r) => r.session_id);
  const visibleSelectedCount = visibleIds.filter((id) =>
    selectedIds.has(id),
  ).length;

  const handleSelectAll = () => {
    const allVisible = visibleIds.length > 0 && visibleSelectedCount === visibleIds.length;
    if (allVisible) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visibleIds));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const exportRows = (list: TrainersReportRow[]) =>
    list.map((row, i) => ({
      Sn: i + 1,
      "Session Name": row.session_name,
      "No of Students": row.student_count,
      "Planned (mins)": row.planned_minutes,
      "Wall-clock (mins)": row.actual_minutes,
      Trainers: row.trainer_count ?? row.trainer_name,
      "Attendance %": row.attendance_percent,
      "Distraction %": row.distraction_percent,
      "Avg Quiz Score": row.avg_quiz_score,
      "Grasping Understood %": row.grasp_understood_percent,
      "Grasping Partial %": row.grasp_partial_percent,
      "Grasping Did Not %": row.grasp_did_not_percent,
      "Avg Ratings": row.avg_rating,
    }));

  const handleExport = () => {
    const source =
      visibleSelectedCount > 0
        ? sortedRows.filter((r) => selectedIds.has(r.session_id))
        : sortedRows;
    if (source.length === 0) return;
    const sheet = XLSX.utils.json_to_sheet(exportRows(source));
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, "Session Report");
    XLSX.writeFile(book, "session-report.xlsx");
  };

  const downloadRow = (row: TrainersReportRow, format: "pdf" | "excel") => {
    if (format === "excel") {
      const sheet = XLSX.utils.json_to_sheet(exportRows([row]));
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, "Session");
      XLSX.writeFile(book, `${row.session_name || "session"}-report.xlsx`);
      return;
    }
    handleViewReport(row);
  };

  const sortableHeader = (
    label: string,
    key: SortKey,
    extra?: React.CSSProperties,
    rowSpan?: number,
  ) => (
    <TableCell
      rowSpan={rowSpan}
      onClick={() => handleSort(key)}
      style={{ ...headerCellStyle, cursor: "pointer", ...extra }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <span>{label}</span>
        <span style={{ marginLeft: 4 }}>{sortIcon(key)}</span>
      </Box>
    </TableCell>
  );

  return (
    <div className="container-fluid p-0 pe-2" style={{ marginTop: 12 }}>
      <Card>
        <CardContent
          style={{
            height: "calc(100vh - 90px)",
            overflow: "hidden",
            padding: 16,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            gap={1}
            mb={1}
            style={{ flexShrink: 0 }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              gap="12px"
              flexWrap="wrap"
            >
              <Box
                display="flex"
                alignItems="center"
                gap="12px"
                flexWrap="wrap"
              >
                <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
                  <InputLabel style={{ fontSize: 14 }}>Track</InputLabel>
                  <Select
                    value={selectedTrack}
                    label="Track"
                    disabled={loadingFilters}
                    onChange={(e) => {
                      setSelectedTrack(e.target.value);
                      setSelectedCourse("");
                      setSelectedBatch("");
                    }}
                    style={selectStyle}
                  >
                    <MenuItem value="">All</MenuItem>
                    {selectedTrack &&
                    !tracks.some((t) => t.track_id === selectedTrack) ? (
                      <MenuItem value={selectedTrack}>{selectedTrack}</MenuItem>
                    ) : null}
                    {tracks.map((t) => (
                      <MenuItem key={t.track_id} value={t.track_id}>
                        {t.track_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
                  <InputLabel style={{ fontSize: 14 }}>Course</InputLabel>
                  <Select
                    value={selectedCourse}
                    label="Course"
                    disabled={!selectedTrack || loadingCourses}
                    onChange={(e) => {
                      setSelectedCourse(e.target.value);
                      setSelectedBatch("");
                    }}
                    style={selectStyle}
                  >
                    <MenuItem value="">All</MenuItem>
                    {selectedCourse &&
                    !courses.some((c) => c.course_id === selectedCourse) ? (
                      <MenuItem value={selectedCourse}>{selectedCourse}</MenuItem>
                    ) : null}
                    {courses.map((c) => (
                      <MenuItem key={c.course_id} value={c.course_id}>
                        {c.course_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
                  <InputLabel style={{ fontSize: 14 }}>Batch</InputLabel>
                  <Select
                    value={selectedBatch}
                    label="Batch"
                    disabled={!selectedCourse || loadingBatches}
                    onChange={(e) => setSelectedBatch(e.target.value)}
                    style={selectStyle}
                  >
                    <MenuItem value="">All</MenuItem>
                    {selectedBatch &&
                    !batches.some((b) => b.batch_id === selectedBatch) ? (
                      <MenuItem value={selectedBatch}>{selectedBatch}</MenuItem>
                    ) : null}
                    {batches.map((b) => (
                      <MenuItem key={b.batch_id} value={b.batch_id}>
                        {b.batch_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
                  <InputLabel style={{ fontSize: 14 }}>Branch</InputLabel>
                  <Select
                    value={selectedBranch}
                    label="Branch"
                    onChange={(e) => setSelectedBranch(e.target.value)}
                    style={selectStyle}
                  >
                    <MenuItem value="">All</MenuItem>
                    {branches.map((b) => (
                      <MenuItem key={b} value={b}>
                        {b}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  type="date"
                  label="Date"
                  InputLabelProps={{ shrink: true }}
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  sx={{
                    minWidth: 150,
                    "& .MuiInputBase-root": { height: 36, fontSize: 14 },
                  }}
                />

                <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
                  <InputLabel style={{ fontSize: 14 }}>Trainer</InputLabel>
                  <Select
                    value={selectedTrainer}
                    label="Trainer"
                    onChange={(e) => setSelectedTrainer(e.target.value)}
                    style={selectStyle}
                  >
                    <MenuItem value="">All</MenuItem>
                    {trainers.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
                  <InputLabel style={{ fontSize: 14 }}>Session Category</InputLabel>
                  <Select
                    value={selectedCategory}
                    label="Session Category"
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    style={selectStyle}
                  >
                    <MenuItem value="">All</MenuItem>
                    {LIVE_SESSION_CATEGORY_OPTIONS.map(
                      (o: { value: string; label: string }) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ),
                    )}
                  </Select>
                </FormControl>

                <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
                  <InputLabel style={{ fontSize: 14 }}>Status</InputLabel>
                  <Select
                    value={selectedStatus}
                    label="Status"
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    style={selectStyle}
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  size="small"
                  onClick={() => void handleApply()}
                  disabled={loadingRows}
                  style={{
                    height: 36,
                    fontSize: 13,
                    textTransform: "none",
                    fontWeight: 600,
                    minWidth: 88,
                  }}
                >
                  {loadingRows ? "Loading…" : "Apply"}
                </Button>
              </Box>

              <Box display="flex" alignItems="center" gap="8px">
                <input
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 4,
                    border: "1px solid #ccc",
                    minWidth: 200,
                    height: 36,
                    fontSize: 14,
                    fontFamily: "inherit",
                    outline: "none",
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleExport}
                  disabled={sortedRows.length === 0}
                  startIcon={<MdFileDownload />}
                  style={{
                    height: 36,
                    fontSize: 12,
                    textTransform: "none",
                    border: "1px solid #ccc",
                    color: "#666",
                    whiteSpace: "nowrap",
                  }}
                >
                  Export{visibleSelectedCount > 0 ? ` (${visibleSelectedCount})` : ""}
                </Button>
              </Box>
            </Box>
          </Box>

          <TableContainer
            component={Paper}
            style={{ marginTop: 8, flexGrow: 1, overflow: "auto" }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    rowSpan={2}
                    style={{
                      ...headerCellStyle,
                      minWidth: 50,
                      textAlign: "center",
                    }}
                  >
                    <Checkbox
                      checked={
                        visibleIds.length > 0 &&
                        visibleSelectedCount === visibleIds.length
                      }
                      indeterminate={
                        visibleSelectedCount > 0 &&
                        visibleSelectedCount < visibleIds.length
                      }
                      onChange={handleSelectAll}
                      size="small"
                    />
                  </TableCell>
                  <TableCell rowSpan={2} style={{ ...headerCellStyle, minWidth: 48 }}>
                    Sn
                  </TableCell>
                  {sortableHeader("Session Name", "session_name", {
                    minWidth: 160,
                  }, 2)}
                  {sortableHeader("No of Students", "student_count", undefined, 2)}
                  <TableCell colSpan={2} style={groupHeaderStyle}>
                    Session Time
                  </TableCell>
                  {sortableHeader("Trainers", "trainer_count", undefined, 2)}
                  {sortableHeader("Attendance %", "attendance_percent", undefined, 2)}
                  {sortableHeader("Distraction %", "distraction_percent", undefined, 2)}
                  {sortableHeader("Avg Quiz Score", "avg_quiz_score", undefined, 2)}
                  <TableCell colSpan={3} style={groupHeaderStyle}>
                    Grasping
                  </TableCell>
                  {sortableHeader("Avg Ratings", "avg_rating", undefined, 2)}
                  <TableCell rowSpan={2} style={{ ...headerCellStyle, textAlign: "center" }}>
                    Report
                  </TableCell>
                  {SHOW_ROW_DOWNLOAD_COLUMN && (
                    <TableCell rowSpan={2} style={{ ...headerCellStyle, textAlign: "center" }}>
                      Download
                    </TableCell>
                  )}
                </TableRow>
                <TableRow>
                  <TableCell
                    onClick={() => handleSort("planned_minutes")}
                    style={{ ...subHeaderStyle, cursor: "pointer" }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      Planned
                      <span style={{ marginLeft: 4 }}>{sortIcon("planned_minutes")}</span>
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort("actual_minutes")}
                    style={{ ...subHeaderStyle, cursor: "pointer" }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      Wall-clock
                      <span style={{ marginLeft: 4 }}>{sortIcon("actual_minutes")}</span>
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort("grasp_understood_percent")}
                    style={{ ...subHeaderStyle, cursor: "pointer" }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      Understood
                      <span style={{ marginLeft: 4 }}>
                        {sortIcon("grasp_understood_percent")}
                      </span>
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort("grasp_partial_percent")}
                    style={{ ...subHeaderStyle, cursor: "pointer" }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      Partial
                      <span style={{ marginLeft: 4 }}>
                        {sortIcon("grasp_partial_percent")}
                      </span>
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort("grasp_did_not_percent")}
                    style={{ ...subHeaderStyle, cursor: "pointer" }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      Did Not
                      <span style={{ marginLeft: 4 }}>
                        {sortIcon("grasp_did_not_percent")}
                      </span>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {error ? (
                  <TableRow>
                    <TableCell colSpan={16} align="center" style={{ padding: 20 }}>
                      <Typography color="error" variant="body1">
                        {error}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : loadingRows ? (
                  <TableRow>
                    <TableCell colSpan={16} align="center" style={{ padding: 20 }}>
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    </TableCell>
                  </TableRow>
                ) : sortedRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={16}
                      align="center"
                      style={{ padding: 20, color: "#666" }}
                    >
                      {appliedOnce
                        ? "No sessions found for these filters."
                        : "Set filters and click Apply to load sessions."}
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedRows.map((row, index) => (
                    <TableRow
                      key={row.session_id || index}
                      style={{
                        backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafafa",
                      }}
                      sx={{
                        "&:hover": {
                          backgroundColor: "#f0f0f0",
                          cursor: "pointer",
                        },
                      }}
                    >
                      <TableCell style={{ ...bodyCellStyle, textAlign: "center" }}>
                        <Checkbox
                          checked={selectedIds.has(row.session_id)}
                          onChange={() => toggleSelect(row.session_id)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell style={bodyCellStyle}>{index + 1}</TableCell>
                      <TableCell style={{ ...bodyCellStyle, fontWeight: 600 }}>
                        {row.session_name}
                      </TableCell>
                      <TableCell style={bodyCellStyle}>
                        {row.student_count ?? "—"}
                      </TableCell>
                      <TableCell style={bodyCellStyle}>
                        {mins(row.planned_minutes)}
                      </TableCell>
                      <TableCell style={bodyCellStyle}>
                        {mins(row.actual_minutes)}
                      </TableCell>
                      <TableCell style={bodyCellStyle}>
                        {row.trainer_count != null && row.trainer_count > 0
                          ? String(row.trainer_count)
                          : row.trainer_name || "—"}
                      </TableCell>
                      <TableCell style={bodyCellStyle}>
                        {pct(row.attendance_percent)}
                      </TableCell>
                      <TableCell style={bodyCellStyle}>
                        {pct(row.distraction_percent)}
                      </TableCell>
                      <TableCell style={bodyCellStyle}>
                        {pct(row.avg_quiz_score)}
                      </TableCell>
                      <TableCell
                        style={graspCellStyle(
                          row.grasp_understood_percent,
                          graspClickCellStyle,
                          bodyCellStyle,
                        )}
                        title={
                          hasPctValue(row.grasp_understood_percent)
                            ? "View Understood feedbacks"
                            : undefined
                        }
                        onClick={
                          hasPctValue(row.grasp_understood_percent)
                            ? () => openGraspFeedbacks(row, "understood")
                            : undefined
                        }
                      >
                        {pct(row.grasp_understood_percent)}
                      </TableCell>
                      <TableCell
                        style={graspCellStyle(
                          row.grasp_partial_percent,
                          graspClickCellStyle,
                          bodyCellStyle,
                        )}
                        title={
                          hasPctValue(row.grasp_partial_percent)
                            ? "View Partial feedbacks"
                            : undefined
                        }
                        onClick={
                          hasPctValue(row.grasp_partial_percent)
                            ? () => openGraspFeedbacks(row, "partial")
                            : undefined
                        }
                      >
                        {pct(row.grasp_partial_percent)}
                      </TableCell>
                      <TableCell
                        style={graspCellStyle(
                          row.grasp_did_not_percent,
                          graspClickCellStyle,
                          bodyCellStyle,
                        )}
                        title={
                          hasPctValue(row.grasp_did_not_percent)
                            ? "View Did Not feedbacks"
                            : undefined
                        }
                        onClick={
                          hasPctValue(row.grasp_did_not_percent)
                            ? () => openGraspFeedbacks(row, "not_understood")
                            : undefined
                        }
                      >
                        {pct(row.grasp_did_not_percent)}
                      </TableCell>
                      <TableCell style={bodyCellStyle}>
                        {pct(row.avg_rating)}
                      </TableCell>
                      <TableCell style={{ ...bodyCellStyle, textAlign: "center" }}>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => handleViewReport(row)}
                          style={{
                            fontSize: 11,
                            padding: "4px 12px",
                            minWidth: 60,
                          }}
                        >
                          VIEW
                        </Button>
                      </TableCell>
                      {SHOW_ROW_DOWNLOAD_COLUMN && (
                        <TableCell style={{ ...bodyCellStyle, textAlign: "center" }}>
                          <Button
                            size="small"
                            onClick={() => downloadRow(row, "pdf")}
                            style={{
                              fontSize: 11,
                              textTransform: "none",
                              minWidth: 0,
                              padding: "2px 6px",
                            }}
                          >
                            pdf
                          </Button>
                          <span style={{ color: "#999", fontSize: 11 }}>/</span>
                          <Button
                            size="small"
                            onClick={() => downloadRow(row, "excel")}
                            style={{
                              fontSize: 11,
                              textTransform: "none",
                              minWidth: 0,
                              padding: "2px 6px",
                            }}
                          >
                            excel
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <GraspFeedbacksDialog
        open={graspFeedbacksOpen}
        onClose={() => setGraspFeedbacksOpen(false)}
        sessionId={graspPopupSessionId}
        sessionName={graspPopupSessionName}
        rows={graspFeedbacks}
        onRowsChange={setGraspFeedbacks}
        initialLevelFilter={graspPopupFilter}
        topicId={null}
        topicTitle={null}
        studentId={null}
      />
    </div>
  );
};

export default TrainersReport;
